import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const AGB = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    document.title = language === 'de' ? 'SourSweet.de/AGB' : 'SourSweet.de/Terms';
  }, [language]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-emerald-800 pt-24 pb-16 px-4 md:px-8 print:!bg-white print:!pt-0 print:!pb-0 print:!px-0 print:!min-h-0" data-testid="agb-page">
      <div className="max-w-4xl mx-auto">
        {/* Content Card */}
        <div className="bg-lime-50 rounded-2xl shadow-xl p-8 md:p-12 print:!shadow-none print:!p-4 print:!rounded-none print:!bg-white">
          {/* Header */}
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-emerald-800 mb-8 text-center print:text-black">
            {language === 'de' ? 'Allgemeine Geschäftsbedingungen (AGB)' : 'General Terms and Conditions (GTC)'}
          </h1>

          {/* Anbieter Info */}
          <div className="bg-emerald-100 rounded-lg p-6 mb-8 print:!bg-gray-100 print:!border print:!border-gray-300">
            <h2 className="font-heading text-xl font-bold text-emerald-700 mb-3 print:text-black">
              {language === 'de' ? 'Anbieter' : 'Provider'}
            </h2>
            <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
              SourSweet<br />
              {language === 'de' ? 'Inhaber' : 'Owner'}: Cem Koptas<br />
              Bärensteinstraße 2<br />
              12685 Berlin<br />
              E-Mail: <a href="mailto:info@soursweet.de" className="text-lime-600 hover:underline print:text-black">info@soursweet.de</a>
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-emerald max-w-none text-emerald-800 print:text-black">
            {/* § 1 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 1 Geltungsbereich' : '§ 1 Scope of Application'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge, Angebote, Lieferungen und Leistungen zwischen SourSweet (nachfolgend „Auftragnehmer") und seinen Kunden (nachfolgend „Auftraggeber"). Abweichende Bedingungen des Auftraggebers finden keine Anwendung, es sei denn, ihrer Geltung wird ausdrücklich und schriftlich zugestimmt.'
                  : 'These General Terms and Conditions (GTC) apply to all contracts, offers, deliveries, and services between SourSweet (hereinafter referred to as the "Contractor") and its customers (hereinafter referred to as the "Client"). Deviating terms and conditions of the Client shall not apply unless expressly agreed in writing.'}
              </p>
            </section>

            {/* § 2 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 2 Vertragsgegenstand' : '§ 2 Subject Matter of the Contract'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Gegenstand des Vertrages sind Dienstleistungen im Bereich Eventorganisation, Eventmanagement, Catering, mobiler Verkauf von Speisen und Getränken sowie damit zusammenhängende Leistungen. Der konkrete Leistungsumfang ergibt sich aus dem jeweiligen Angebot oder der schriftlichen Vereinbarung.'
                  : 'The subject of the contract includes services in the areas of event organization, event management, catering, mobile sale of food and beverages, and related services. The specific scope of services results from the respective offer or written agreement.'}
              </p>
            </section>

            {/* § 3 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 3 Vertragsschluss' : '§ 3 Conclusion of Contract'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Ein Vertrag kommt zustande, sobald der Auftraggeber ein Angebot des Auftragnehmers schriftlich bestätigt (z. B. per E-Mail oder Unterschrift).'
                  : 'A contract is concluded as soon as the Client confirms an offer from the Contractor in writing (e.g. by email or signature).'}
              </p>
            </section>

            {/* § 4 */}
            <section className="mb-8 print:break-before-page">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 4 Preise und Zahlungsbedingungen' : '§ 4 Prices and Payment Terms'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Alle Preise verstehen sich in Euro zuzüglich der gesetzlichen Umsatzsteuer, sofern nicht ausdrücklich anders angegeben.'
                  : 'All prices are stated in euros plus statutory value-added tax, unless expressly stated otherwise.'}
              </p>
            </section>

            {/* § 5 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 5 Anzahlungen und Zahlungsfälligkeit' : '§ 5 Deposits and Payment Due Date'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Der Auftragnehmer ist berechtigt, bei Vertragsschluss eine angemessene Anzahlung zu verlangen. Die vollständige Zahlung ist Voraussetzung für die Durchführung der Veranstaltung. Die Durchführung der Veranstaltung setzt voraus, dass der vollständige Rechnungsbetrag vor Veranstaltungsbeginn auf dem Konto des Auftragnehmers eingegangen ist.'
                  : 'The Contractor is entitled to request an appropriate deposit upon conclusion of the contract. Full payment is a prerequisite for the execution of the event. The event will only be carried out if the full invoice amount has been credited to the Contractor\'s account prior to the start of the event.'}
              </p>
            </section>

            {/* § 6 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 6 Änderungen des Leistungsumfangs' : '§ 6 Changes to the Scope of Services'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Änderungen der Teilnehmerzahl oder des vereinbarten Leistungsumfangs sind bis spätestens 5 Kalendertage vor Veranstaltungsbeginn kostenfrei im Rahmen einer Toleranz von ±10% möglich. Weitergehende Änderungen oder kurzfristige Anpassungen können zu Mehrkosten führen.'
                  : 'Changes to the number of participants or the agreed scope of services are possible free of charge up to no later than 5 calendar days before the start of the event within a tolerance of ±10%. Further changes or short-notice adjustments may result in additional costs.'}
              </p>
            </section>

            {/* § 7 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 7 Stornierung und Rücktritt durch den Auftraggeber' : '§ 7 Cancellation and Withdrawal by the Client'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed mb-4 print:text-black">
                {language === 'de' 
                  ? 'Es gelten folgende verbindliche Stornierungspauschalen:'
                  : 'The following binding cancellation fees apply:'}
              </p>
              <ul className="list-disc list-inside text-emerald-700 font-medium space-y-2 ml-4 print:text-black">
                <li>{language === 'de' ? 'Bis 30 Kalendertage vor Veranstaltungsbeginn: 20%' : 'Up to 30 calendar days before the start of the event: 20%'}</li>
                <li>{language === 'de' ? 'Bis 14 Kalendertage vor Veranstaltungsbeginn: 50%' : 'Up to 14 calendar days before the start of the event: 50%'}</li>
                <li>{language === 'de' ? 'Ab 7 Kalendertage vor Veranstaltungsbeginn oder Nichterscheinen: 100%' : 'From 7 calendar days before the start of the event or no-show: 100%'}</li>
              </ul>
              <p className="text-emerald-700 font-medium leading-relaxed mt-4 print:text-black">
                {language === 'de' 
                  ? 'Unabhängig vom Zeitpunkt der Stornierung sind bereits entstandene oder verbindlich beauftragte Kosten für Fremdleistungen (z. B. Technik, Künstler, Mietmaterialien, Waren) vom Auftraggeber zu 100% zu erstatten.'
                  : 'Regardless of the time of cancellation, costs already incurred or bindingly commissioned for third-party services must be reimbursed by the Client at 100%.'}
              </p>
            </section>

            {/* § 8 */}
            <section className="mb-8 print:break-before-page">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 8 Mitwirkungspflichten, Genehmigungen und GEMA' : '§ 8 Duties of Cooperation, Permits and GEMA'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Der Auftraggeber ist verpflichtet, alle für die Durchführung der Veranstaltung erforderlichen Informationen rechtzeitig bereitzustellen. Der Auftraggeber trägt die alleinige Verantwortung für behördliche Genehmigungen sowie für die Anmeldung bei der GEMA und die Zahlung aller anfallenden Gebühren.'
                  : 'The Client is obliged to provide all information required for the execution of the event in a timely manner. The Client bears sole responsibility for official permits as well as for registration with GEMA and payment of all applicable fees.'}
              </p>
            </section>

            {/* § 9 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 9 Versicherungspflicht des Auftraggebers' : '§ 9 Insurance Obligation of the Client'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Der Auftraggeber verpflichtet sich, für die Dauer der Veranstaltung eine angemessene Haftpflichtversicherung abzuschließen, welche Personen-, Sach- und Vermögensschäden abdeckt. Der Auftragnehmer ist berechtigt, einen Versicherungsnachweis anzufordern.'
                  : 'The Client undertakes to take out adequate liability insurance for the duration of the event, covering personal injury, property damage, and financial loss. The Contractor is entitled to request proof of insurance.'}
              </p>
            </section>

            {/* § 10 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 10 Catering, Reinigung und Abfall' : '§ 10 Catering, Cleaning and Waste'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Der Auftragnehmer übernimmt eine übliche Endreinigung im Rahmen der vereinbarten Leistungen. Zusätzliche Reinigungsarbeiten können gesondert in Rechnung gestellt werden. Der Auftraggeber ist verpflichtet, bekannte Allergien oder Unverträglichkeiten der Gäste rechtzeitig schriftlich mitzuteilen.'
                  : 'The Contractor performs a standard final cleaning within the agreed services. Additional cleaning work exceeding the usual level may be charged separately. The Client is obliged to inform the Contractor in writing of known allergies or intolerances of guests in good time before the event.'}
              </p>
            </section>

            {/* § 11 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 11 Equipment, Rückgabe und Schäden' : '§ 11 Equipment, Return and Damage'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Bereitgestelltes Equipment ist unmittelbar nach Veranstaltungsende vollständig und unbeschädigt zu übergeben. Bei Verlust oder Beschädigung erfolgt eine Abrechnung zum Wiederbeschaffungswert.'
                  : 'Provided equipment must be returned immediately after the end of the event, complete and undamaged. In the event of loss or damage, billing will be based on the replacement value of an equivalent new product.'}
              </p>
            </section>

            {/* § 12 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 12 Haftung und Lebensmittelsicherheit' : '§ 12 Liability and Food Safety'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Bei Catering-Leistungen geht die Verantwortung für Lagerung und Verzehr spätestens zwei Stunden nach Bereitstellung auf den Auftraggeber über.'
                  : 'For catering services, responsibility for proper storage, cooling, and consumption of food transfers to the Client no later than two hours after provision. The Contractor is not liable for health impairments resulting from improper storage or delayed consumption.'}
              </p>
            </section>

            {/* § 13 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 13 Datenschutz sowie Bild- und Videoaufnahmen' : '§ 13 Data Protection and Photo/Video Recordings'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Die Verarbeitung personenbezogener Daten sowie die Nutzung von Foto- und Videoaufnahmen erfolgt im Einklang mit der DSGVO.'
                  : 'The processing of personal data and the use of photo and video recordings are carried out in accordance with the General Data Protection Regulation (GDPR).'}
              </p>
            </section>

            {/* § 14 */}
            <section className="mb-8 print:break-before-page">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 14 Vertragsübertragung' : '§ 14 Transfer of Contract'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Eine Übertragung der Rechte und Pflichten auf Dritte ist nur mit schriftlicher Zustimmung des Auftragnehmers zulässig.'
                  : 'Transfer of rights and obligations from the contractual relationship to third parties is only permitted with the prior written consent of the Contractor. The original Client remains jointly and severally liable for all obligations.'}
              </p>
            </section>

            {/* § 15 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 15 Alkoholausschank und Jugendschutz' : '§ 15 Alcohol Service and Youth Protection'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Die gesetzlichen Bestimmungen des Jugendschutzgesetzes (JuSchG) sind strikt einzuhalten.'
                  : 'The statutory provisions of the Youth Protection Act (JuSchG) must be strictly observed. The serving and consumption of alcoholic beverages to persons under the age of 18 is prohibited. The Client is responsible for taking appropriate measures to ensure compliance. The Contractor assumes no liability for violations caused by guests or third parties.'}
              </p>
            </section>

            {/* § 16 */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-emerald-700 mb-4 print:text-black">
                {language === 'de' ? '§ 16 Schlussbestimmungen' : '§ 16 Final Provisions'}
              </h2>
              <p className="text-emerald-700 font-medium leading-relaxed print:text-black">
                {language === 'de' 
                  ? 'Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Berlin, soweit zulässig.'
                  : 'The law of the Federal Republic of Germany shall apply. Place of jurisdiction is Berlin, insofar as legally permissible. Should any provision of these GTC be invalid, the validity of the remaining provisions shall remain unaffected.'}
              </p>
            </section>

            {/* Last Updated */}
            <p className="text-emerald-600 font-semibold text-sm mt-8 print:text-gray-600">
              {language === 'de' ? 'Zuletzt aktualisiert: Dezember 2024' : 'Last updated: December 2024'}
            </p>
          </div>

          {/* Buttons - Hidden on Print */}
          <div className="flex flex-col sm:flex-row gap-4 mt-10 print:hidden">
            <button
              onClick={handleClose}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-700 text-white hover:bg-emerald-600 hover:shadow-xl hover:shadow-emerald-400/50 transition-all duration-300 rounded-lg px-6 py-3 font-bold"
              data-testid="close-button"
            >
              <X size={20} />
              {language === 'de' ? 'Schließen' : 'Close'}
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 bg-lime-200 text-emerald-800 hover:bg-lime-300 hover:shadow-xl hover:shadow-lime-300/50 transition-all duration-300 rounded-lg px-6 py-3 font-bold"
              data-testid="print-button"
            >
              <Printer size={20} />
              {language === 'de' ? 'Drucken' : 'Print'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
